import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from './api';
import { calendarDay, formatInZone } from '../design/timeZone';
import { useRefreshTick } from '../hooks/usePolling';

// Dashboard routes (A012, docs/design/contracts.md §7 Dashboards). `params` are the shell filter's
// { from, to, hierarchyNodeIds | communityIds } plus page-specific keys; the API applies the caller's scope.
const get = async (path, params) => (await apiClient.get(path, { params })).data;

export const analyticsApi = {
  overview: (params, compare = true) => get('/analytics/overview', compare ? { ...params, compare: 'previous' } : params),
  heatmap: (params) => get('/analytics/heatmap', params),
  sla: (params) => get('/analytics/sla', params),
  callFlow: (params) => get('/analytics/call-flow', params),
  agents: (params) => get('/agent/analytics', params),
  agent: (agentId, params) => get(`/agent/${encodeURIComponent(agentId)}/analytics`, params),
  queues: (params) => get('/queue/analytics', params),
  queue: (queueId, params) => get(`/queue/${encodeURIComponent(queueId)}/analytics`, params),
  wallboard: (params) => get('/wallboard/current', params),
};

/**
 * Loads a dashboard whenever `key` (a string identifying the request) changes. Keeps the previous data while a
 * new request runs so the page doesn't flash; `error` holds the axios error for ErrorState. Re-queries in the
 * background on `useRefreshTick` (every 5 minutes while visible, and on return to the tab): a background load
 * leaves `loading` alone, replaces the data only when it succeeds and is dropped when a newer load starts.
 */
export function useAnalytics(load, key) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const loadRef = useRef(load);
  loadRef.current = load;
  const [reloads, setReloads] = useState(0);
  const latest = useRef(0);
  const foreground = useRef(false);
  const tick = useRefreshTick(key);

  useEffect(() => {
    const request = ++latest.current;
    foreground.current = true;
    setState((current) => ({ ...current, loading: true }));
    loadRef
      .current()
      .then((data) => {
        if (request === latest.current) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (request === latest.current) setState({ data: null, error, loading: false });
      })
      .finally(() => {
        if (request === latest.current) foreground.current = false;
      });
  }, [key, reloads]);

  useEffect(() => {
    if (!tick || foreground.current) return;
    const request = ++latest.current;
    loadRef
      .current()
      .then((data) => {
        if (request === latest.current) setState({ data, error: null, loading: false });
      })
      .catch(() => {});
  }, [tick]);

  useEffect(() => () => {
    latest.current += 1;
  }, []);

  const retry = useCallback(() => setReloads((n) => n + 1), []);
  return { ...state, retry };
}

/* ---- view helpers shared by the dashboard pages (mockup formats from shell-data.js) ---- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isoDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/** "Fri 9" (overview daily axis). */
export const dayLabel = (iso) => {
  const d = isoDate(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()}`;
};

/** "Oct 9". */
export const shortDate = (iso) => {
  const d = isoDate(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** "Fri Oct 9". */
export const weekdayDate = (iso) => {
  const d = isoDate(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

export const isWeekday = (iso) => {
  const day = isoDate(iso).getUTCDay();
  return day !== 0 && day !== 6;
};

/** "Oct 9 – Oct 15". */
export const rangeText = (from, to) => (from && to ? (from === to ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`) : '');

/** Days in an inclusive ISO range. */
export const dayCount = (from, to) => (from && to ? Math.round((isoDate(to) - isoDate(from)) / 86400000) + 1 : 0);

/** First ISO day of the previous period of equal length (the API's `compare=previous`). */
export const previousPeriodStart = (from, to) => (from && to ? new Date(isoDate(from).getTime() - dayCount(from, to) * 86400000).toISOString().slice(0, 10) : null);

export const BEFORE_HISTORY = 'Before history';
export const NO_PRIOR_DATA = 'No prior data';

/**
 * Why a "vs prior" delta can't be shown, or null when it can: BEFORE_HISTORY when the previous period starts before
 * the account's call history (`shell/status` callHistoryStartDate, TECH_DEBT #52), NO_PRIOR_DATA when the previous
 * period has nothing to compare (no offered calls, or no figure).
 */
export function priorGap({ from, to, historyStartDate, hasPrior }) {
  const start = previousPeriodStart(from, to);
  if (historyStartDate && start && start < historyStartDate) return BEFORE_HISTORY;
  return hasPrior ? null : NO_PRIOR_DATA;
}

/** 7 → "7a", 12 → "12p", 13 → "1p". */
export const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;

const clock = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;

/** 10 → "10:00 AM". */
export const hourClock = (h) => clock(h % 24);

/** The mockups' business-hours axis (default 7 AM to 7 PM), widened to include any hour with data. */
export function hourRange(valuesByHour, from = 7, to = 19) {
  let lo = from;
  let hi = to;
  (valuesByHour || []).forEach((v, h) => {
    if (v) {
      lo = Math.min(lo, h);
      hi = Math.max(hi, h);
    }
  });
  const hours = [];
  for (let h = lo; h <= hi; h += 1) hours.push(h);
  return hours;
}

/** "9:52 AM" in the account time zone. */
export const clockTime = (utc) => (utc ? formatInZone(new Date(utc), { hour: 'numeric', minute: '2-digit' }) : '');

/** ISO date of an instant in the account time zone. */
export const localIso = (utc) => calendarDay(new Date(utc));

/** "Thu, Oct 15" (call tables). */
export const callDate = (utc) => {
  const d = isoDate(localIso(utc));
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** "9:52 AM" today, "Yesterday", else "Wed, Oct 14" (mockup attention list). */
export function whenText(utc, now = new Date()) {
  if (!utc) return '';
  const day = localIso(utc);
  if (day === localIso(now)) return clockTime(utc);
  if (day === localIso(new Date(now.getTime() - 86400000))) return 'Yesterday';
  return callDate(utc);
}

/** Presence status from the API → mockup presence class (p-*). Teams "Busy" without a call uses the wrap-up swatch. */
export const PRESENCE_CLASS = { available: 'available', oncall: 'busy', busy: 'wrap', away: 'away', offline: 'offline' };

/** API sentiment level → the four mockup badges. */
export const sentimentName = (level) =>
  ({ VeryNegative: 'Negative', Negative: 'Negative', Neutral: 'Neutral', Mixed: 'Mixed', Positive: 'Positive', VeryPositive: 'Positive' }[level] || null);

/** JourneyStepType → route strip type. */
export const ROUTE_TYPE = { AutoAttendant: 'aa', Queue: 'queue', Agent: 'agent', Transfer: 'transfer', Voicemail: 'voicemail', Forward: 'forward', Abandoned: 'abandon', External: 'external' };

export const routeSteps = (steps) => (steps || []).map((s) => ({ type: ROUTE_TYPE[s.type] || 'external', name: s.name || s.type }));

/** Severity enum → ClassificationTag/SeverityLabel key. */
export const severityKey = (severity) => (severity === 'Within48Hours' ? 'Within 48 Hours' : severity || 'Informational');

/** "SLA: 80% answered within 60 seconds, business hours only". */
export function slaSentence(target, businessHoursOnly) {
  if (!target) return '';
  const s = target.answerWithinSeconds;
  return `SLA: ${Number(target.answerTargetPercent)}% answered within ${s} ${s === 1 ? 'second' : 'seconds'}${businessHoursOnly ? ', business hours only' : ''}`;
}

export const seriesValues = (series) => (series || []).map((p) => Number(p.value));

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;

export const sum = (values) => values.reduce((a, b) => a + (Number(b) || 0), 0);

/** "A", "A and B", or "A and 3 more" (one line in a KPI). */
export const nameList = (names) => (names.length <= 2 ? names.join(' and ') : `${names[0]} and ${names.length - 1} more`);
